/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { useState } from "react";
import * as React from "react";
import styled from "styled-components";
import { webComponentShim } from "components/shared/WebComponentShim";
import { useCurrentUser } from "actions/CurrentUserActions";
import useDropWaveformBlockOrNativeFile, {
  makeMultiFileDropHandler,
} from "hooks/useDropWaveformBlockOrNativeFile";
import addAudioIllustration from "images/EnhanceSpeech/add-audio-illustration.svg";
import { devices, fontFamilies, useIsMobile } from "components/shared/Styles";
import { EnhancingSpinner } from "../Spinner";
import {
  BasicDownloadButton,
  ButtonSecondaryXL,
  UploadButton,
} from "../Buttons";
import { useEnhanceSpeechLimits } from "components/EnhancedSpeech/useEnhanceSpeechLimits";
import { EnhancementObject, ProcessingState } from "../useEnhanceSpeech";
import { DropzoneText } from "../Dropzone";
import {
  CompletedEnhanceSpeechTrack,
  EnhanceSpeechTrackErrorType,
  isEnhanceSpeechCompletedSuccessfully,
  isLimitReachedError,
  LimitReachedEnhanceSpeechTrackErrorType,
  userMessageForEnhanceTrackError,
} from "components/EnhancedSpeech/Utilities";
import { BasicEnhancedTrackPlayer } from "components/EnhancedSpeech/BasicEnhancedTrackPlayer";
import { useGlobalEnhanceSpeechTimeout } from "components/EnhancedSpeech/FreeTierEnhanceSpeech/useGlobalEnhanceSpeechTimeout";
import LoggedOutContent from "./LoggedOutContent";
import { LimitReached } from "./LimitReached";
import { PlatformContent } from "./Platforms";
import { SWCTooltip } from "components/shared/Tooltips";
import Analytics, { AnalyticsEvent } from "constants/Analytics";
import { useDispatch } from "react-redux";
import { addToast } from "actions/ToastMessageActions";
import { ModelVersionPicker } from "components/EnhancedSpeech/ModelVersionPicker";
import { ModelVersion } from "components/EnhancedSpeech/useEnhanceSpeech/ModelVersion";
import {
  ACCEPTED_CONTENT_TYPES,
  ACCEPTED_VIDEO_CONTENT_TYPES,
} from "constants/AcceptedFiles";
import "stylesheets/spectrum.scss";

const PreviewActionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  margin-top: 26px;
`;

const FeedbackLink = styled.a.attrs({
  className: "spectrum-Link",
})`
  font-size: 16px;
  font-weight: 400;
  margin-top: 24px;
`;

const UploadButtonWrapper = styled.div`
  padding-bottom: 32px;
`;

const UploadContainer = styled.div`
  padding-bottom: 32px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  min-width: 210px;
`;

const ModelVersionPickerContainer = styled.div`
  margin-right: 4px;
`;

const EnhanceLimits = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 16px;
  /* theses are need for very specific cases where we
  need to wrap two elements and hide spacers */
  @media (min-width: 769px) and (max-width: 1077px) {
    width: 300px;
  }
  @media (max-width: 556px) {
    width: 400px;
  }
  ${devices.small} {
    margin-top: 0px;
    margin-bottom: 16px;
  }
`;

const EnhanceLimitColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 112px;
  padding: 7px 0px;
  text-align: center;

  ${devices.small} {
    width: 125px;
  }
`;

const EnhanceLimitValue = styled.div<{ marginTop?: string }>`
  margin-top: ${({ marginTop }): string => marginTop ?? "0px"};
  font-size: 16px;
  line-height: 20px;
  font-weight: bold;
  color: #222222;
`;

const EnhanceLimitDescription = styled.div`
  font-size: 14px;
  line-height: 17px;
  color: #222222;
  margin-top: 4px;
`;

const EnhanceLimitSpacer = styled.div`
  width: 3px;
  height: 64px;
  background-color: #dddcff;
  border-radius: 2px;
  /* theses are need for very specific cases where we
  need to wrap two elements and hide spacers */
  @media (min-width: 769px) and (max-width: 1077px) {
    display: none;
  }
`;

const EnhanceLimit: React.FC<{
  limitDescription: string;
  limitValue: string;
}> = ({ limitDescription, limitValue }) => {
  return (
    <EnhanceLimitColumn>
      <EnhanceLimitValue>{limitValue}</EnhanceLimitValue>
      <EnhanceLimitDescription>{limitDescription}</EnhanceLimitDescription>
    </EnhanceLimitColumn>
  );
};

const FormatsButton = styled(webComponentShim("sp-action-button")).attrs({
  size: "s",
  slot: "trigger",
})`
  margin-top: 2px;
  cursor: pointer;
`;

const EnhanceFormats: React.FC<{
  formatDescription: string;
  formatListDescription: string;
}> = ({ formatDescription, formatListDescription }) => {
  return (
    <EnhanceLimitColumn>
      <EnhanceLimitValue marginTop="4px">{formatDescription}</EnhanceLimitValue>
      <overlay-trigger placement="bottom">
        <FormatsButton>{I18n.t("enhance_speech.file_types")}</FormatsButton>
        <SWCTooltip slot="hover-content" open placement="bottom">
          {formatListDescription}
        </SWCTooltip>
      </overlay-trigger>
    </EnhanceLimitColumn>
  );
};

const PlayerContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 72px;

  ${devices.medium} {
    padding: 0;
  }

  ${devices.small} {
    padding: 16px 0;
  }
`;

export const PlayerHeading = styled.h3`
  font: normal normal 900 32px/40px ${fontFamilies};
  color: var(--spectrum-global-color-static-gray-900);

  ${devices.small} {
    font-size: 28px;
    line-height: 35px;
  }
`;

const PlayerContent: React.FC<{
  enhancement: EnhancementObject;
  onLoadingSuccess: () => void;
  onLoadingFailed: (error: MediaError | null) => void;
}> = ({ enhancement, onLoadingSuccess, onLoadingFailed }) => {
  const enhancedTrack = enhancement.track as CompletedEnhanceSpeechTrack;
  const { currentUser } = useCurrentUser();

  const enhanceSpeechV2Enabled = Boolean(
    currentUser?.enhanceSpeechV2FreeEnabled
  );

  const clearEnhancement = (): void => {
    enhancement.delete();
  };

  return (
    <PlayerContentWrapper>
      <PlayerHeading>
        {I18n.t("enhance_speech.preview_and_download")}
      </PlayerHeading>
      <BasicEnhancedTrackPlayer
        track={enhancedTrack}
        onLoadingSuccess={onLoadingSuccess}
        onLoadingFailed={onLoadingFailed}
      />
      <PreviewActionsContainer>
        <BasicDownloadButton track={enhancedTrack} />
        <ButtonSecondaryXL
          disabled={enhancement.isDeleting ? true : undefined}
          onClick={clearEnhancement}
        >
          {I18n.t("enhance_speech.upload_another")}
        </ButtonSecondaryXL>
      </PreviewActionsContainer>
      {!enhanceSpeechV2Enabled && (
        <FeedbackLink target="_blank" href="/enhance-speech-v2">
          {I18n.t("enhance_speech_v2_early_access_page.promo_link_text")}
        </FeedbackLink>
      )}
    </PlayerContentWrapper>
  );
};

const AwaitingFileContentContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  flex-direction: column;
  align-items: center;
`;

const AwaitingFileContent: React.FC<{
  onFilesSelected: (selectedFiles: FileList) => void;
  modelVersion: ModelVersion;
  setModelVersion: (modelVersion: ModelVersion) => void;
}> = ({ onFilesSelected, modelVersion, setModelVersion }) => {
  const limits = useEnhanceSpeechLimits();
  const isMobile = useIsMobile();
  const { currentUser } = useCurrentUser();
  const enhanceSpeechV2FreeEnabled = Boolean(
    currentUser?.enhanceSpeechV2FreeEnabled
  );

  return (
    <AwaitingFileContentContainer>
      <img src={addAudioIllustration} />
      <DropzoneText marginTopPx={16} marginBottomPx={12}>
        <span>{I18n.t("enhance_speech.drop_files_instruction")}</span>
      </DropzoneText>
      {enhanceSpeechV2FreeEnabled ? (
        <UploadContainer>
          <ModelVersionPickerContainer>
            <ModelVersionPicker
              modelVersion={modelVersion}
              onSelected={setModelVersion}
            />
          </ModelVersionPickerContainer>
          <UploadButton
            onFilesSelected={onFilesSelected}
            title={I18n.t("enhance_speech.choose_files")}
          />
        </UploadContainer>
      ) : (
        <UploadButtonWrapper>
          <UploadButton
            onFilesSelected={onFilesSelected}
            title={I18n.t("enhance_speech.choose_files")}
          />
        </UploadButtonWrapper>
      )}
      <EnhanceLimits>
        {!isMobile && (
          <>
            <EnhanceFormats
              formatDescription={I18n.t("enhance_speech.audio_only")}
              formatListDescription={
                ".wav, .mp3, .aac, .flac, .ogg, .oga, .m4a"
              }
            />
            <EnhanceLimitSpacer />
          </>
        )}
        <EnhanceLimit
          limitDescription={I18n.t("enhance_speech.max_duration")}
          limitValue={limits.fileDurationLimitDescription}
        />
        <EnhanceLimitSpacer />
        <EnhanceLimit
          limitDescription={I18n.t("enhance_speech.max_size")}
          limitValue={limits.fileSizeLimitDescription}
        />
        <EnhanceLimitSpacer />
        <EnhanceLimit
          limitDescription={I18n.t("enhance_speech.total_daily_limit")}
          limitValue={limits.dailyDurationLimitDescription}
        />
      </EnhanceLimits>
    </AwaitingFileContentContainer>
  );
};

const LoggedInContent: React.FC<{
  enhancement: EnhancementObject | undefined;
  processFile: (droppedFile: File, modelVersion: ModelVersion) => void;
}> = ({ enhancement, processFile }) => {
  const dispatch = useDispatch();
  const userLimits = useEnhanceSpeechLimits();
  const enhanceSpeechTrack = enhancement?.track;
  const [multiFileErrorType, setMultiFileErrorType] =
    useState<EnhanceSpeechTrackErrorType | null>(null);

  const { currentUser } = useCurrentUser();
  const enhanceSpeechV2FreeEnabled = Boolean(
    currentUser?.enhanceSpeechV2FreeEnabled
  );

  const [modelVersion, setModelVersion] = useState<ModelVersion>(
    enhanceSpeechV2FreeEnabled ? ModelVersion.v2 : ModelVersion.v1
  );

  const { isDraggingOver, dropRef, filePickerHandler } =
    useDropWaveformBlockOrNativeFile({
      acceptedContentTypes: ACCEPTED_CONTENT_TYPES,
      onFileDroppedHandler: makeMultiFileDropHandler((droppedFiles: File[]) => {
        if (droppedFiles.length > 1) {
          setMultiFileErrorType(EnhanceSpeechTrackErrorType.MULTI_FILE_LIMIT);
        } else if (!ACCEPTED_CONTENT_TYPES.includes(droppedFiles[0].type)) {
          dispatch(
            addToast({
              message: userMessageForEnhanceTrackError(
                EnhanceSpeechTrackErrorType.UNSUPPORTED_EXTENSION,
                userLimits,
                null
              ),
            })
          );
        } else if (
          ACCEPTED_VIDEO_CONTENT_TYPES.includes(droppedFiles[0].type)
        ) {
          Analytics.instance.logEvent(
            AnalyticsEvent.USER_TRIGGERS_VIDEO_UPSELL
          );
          setMultiFileErrorType(EnhanceSpeechTrackErrorType.VIDEO_NOT_ACCEPTED);
        } else {
          processFile(droppedFiles[0], modelVersion);
        }
      }),
    });

  const { onLoadingSuccess, onLoadingFailed, hasTimedOut } =
    useGlobalEnhanceSpeechTimeout(enhancement);

  const isBusy =
    enhancement !== undefined &&
    enhancement.processingState !== ProcessingState.finished;
  const isFinished =
    enhancement !== undefined &&
    enhancement.processingState === ProcessingState.finished;

  const enhanceTrackErrorType =
    multiFileErrorType || enhancement?.track?.errorType;

  const backgroundColor =
    isDraggingOver && !isBusy
      ? "rgba(var(--spectrum-global-color-static-indigo-600-rgb), 0.16)"
      : "none";

  const defaultContent = (
    <AwaitingFileContent
      onFilesSelected={filePickerHandler}
      modelVersion={modelVersion}
      setModelVersion={setModelVersion}
    />
  );

  // Initial state
  if (
    (!enhanceSpeechTrack && !enhanceTrackErrorType) ||
    (hasTimedOut && !enhanceTrackErrorType)
  ) {
    return (
      <PlatformContent dropZoneRef={dropRef} backgroundColor={backgroundColor}>
        {defaultContent}
      </PlatformContent>
    );
  }

  const ref = isBusy ? null : dropRef;

  let content = defaultContent;
  let completedEnhanceSpeechTrack: CompletedEnhanceSpeechTrack | undefined;

  if (
    enhanceSpeechTrack &&
    isEnhanceSpeechCompletedSuccessfully(enhanceSpeechTrack)
  ) {
    completedEnhanceSpeechTrack = enhanceSpeechTrack;

    content = (
      <PlayerContent
        enhancement={enhancement}
        onLoadingSuccess={onLoadingSuccess}
        onLoadingFailed={onLoadingFailed}
      />
    );
  }

  if (isBusy && !hasTimedOut) {
    content = (
      <EnhancingSpinner
        isUploading={enhancement.processingState == ProcessingState.uploading}
        percentUploaded={enhancement?.percentUploaded}
      />
    );
  }

  if (enhanceTrackErrorType && isLimitReachedError[enhanceTrackErrorType]) {
    content = (
      <LimitReached
        track={enhancement?.track}
        enhanceTrackErrorType={
          enhanceTrackErrorType as LimitReachedEnhanceSpeechTrackErrorType
        }
        removeEnhancement={async (): Promise<void> => {
          if (multiFileErrorType) {
            setMultiFileErrorType(null);
          } else {
            enhancement?.delete();
          }
        }}
      />
    );
  }

  return (
    <PlatformContent
      dropZoneRef={ref}
      backgroundColor={backgroundColor}
      isBusy={isBusy}
      isFinished={isFinished}
      hasTrackError={Boolean(enhanceTrackErrorType)}
      completedEnhanceSpeechTrack={completedEnhanceSpeechTrack}
    >
      {content}
    </PlatformContent>
  );
};

const FreeEnhanceSpeechContainer: React.FC<{
  enhancement: EnhancementObject | undefined;
  processFile: (droppedFile: File, modelVersion: ModelVersion) => void;
}> = ({ enhancement, processFile }) => {
  const { currentUser } = useCurrentUser();

  if (!currentUser) {
    return <LoggedOutContent />;
  } else {
    return (
      <LoggedInContent enhancement={enhancement} processFile={processFile} />
    );
  }
};

export default FreeEnhanceSpeechContainer;
